/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Product Select Subscribers Table
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.10.0
 */

import { useEffect } from 'react';

import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Table } from '@components/common';
import {
  loadTableListAction,
  setTableSortAction,
  switchCheckbox,
} from '@components/Table/actions/tableActions';
import type {
  ITableField,
  ITableReducer,
} from '@components/Table/reducers/tableReducer';
import {
  makeSelectorTableBasketAllIn,
  makeSelectorTableCount,
  makeSelectorTableData,
  makeSelectorTableFields,
  makeSelectorTableIsLoading,
  makeSelectorTableSort,
} from '@components/Table/selectors/tableSelectors';
import TableColumnUtil from '@components/Table/TableColumnUtil';
import type {
  ILoadTableListPayload,
  ISetTableSortPayload,
  ISwitchCheckboxPayload,
  TLoadTableListAction,
  TSwitchCheckboxAction,
} from '@components/Table/types/tableTypes';
import { TableNameType } from '@components/Table/types/tableTypes';
import {
  makeSelectTranslationCommon,
  makeSelectTranslationWizardProductOfferings,
} from '@modules/internationalization/intSelectors';
import type {
  ICommonPageTranslation,
  ICommonTranslation,
  IWizardProductOfferingsTranslation,
} from '@modules/internationalization/types';
import type { ICommonPageTranslationTitle } from '@modules/internationalization/types/pages/common';
import type { IProductSelectSubscribersTableTranslation } from '@modules/internationalization/types/wizards/productOfferings';
import { guardComponentType } from '@modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '@modules/role/selectors/guardSelector';
import { makeSelectIsDensity } from '@modules/storage/storageSelectors';
import type { IRootReducer } from '@reducers/rootReducer';

import { productOfferingsStartValidateAction } from '../../wizardProductOfferingsActions';
import { EProductSubscribersTableField } from '../../wizardProductOfferingsConstants';
import {
  makeSelectProductOfferingsIsTariff,
  makeSelectProductOfferingsSubsCount,
} from '../../wizardProductOfferingsSelectors';
import {
  getColumnCellType,
  getColumnFilterType,
  getColumnFixedType,
  productSelectSubscribersColumnNames,
  productSelectSubscribersColumnsWidth,
} from './ProductSelectSubscribersTableCells';

export type TSelectSubscribersTableProps = IConnectedProps & IDispatchProps;

const ProductSelectSubscribersTable = (props: TSelectSubscribersTableProps) => {
  const { basketAllIn, isTariff, startValidateAction, subsCount } = props;

  useEffect(() => {
    startValidateAction();
  }, [startValidateAction, subsCount]);

  const tableColumnUtil = new TableColumnUtil<
    EProductSubscribersTableField,
    ICommonPageTranslation<IProductSelectSubscribersTableTranslation> &
      ICommonTranslation
  >({
    cellConfig: productSelectSubscribersColumnsWidth,
    componentsFeature: props.componentsFeature,
    fields: props.fields as Array<ITableField<EProductSubscribersTableField>>,
    // disable column resize - server side does not support resize api for this end point
    isTableResizeEnabled: false,
    getColumnCellType,
    getColumnFilterType,
    getColumnFixedType,
    columnNames: productSelectSubscribersColumnNames.filter(
      (name) => !isTariff && name === EProductSubscribersTableField.RATE_PLAN,
    ),
    translation: {
      ...props.translationCommon,
      table: { ...props.productOfferingsTranslation.table },
      title: {} as ICommonPageTranslationTitle,
    },
  });

  return (
    <>
      <Table
        basketAllIn={basketAllIn}
        columns={tableColumnUtil.customizeTableColumns()}
        counter={props.tableCount}
        data={props.data}
        disableSortCheckbox={false}
        isDensity={props.isDensity}
        isLoading={props.isLoading}
        loadTableList={props.loadTableList}
        scrollbarBottom="64px"
        setTableSort={props.setTableSort}
        showCheckedColumn={true}
        sort={props.sort}
        switchCheckbox={props.switchCheckbox}
        tableDataName="ProductSelectSubscribersTable"
      />
    </>
  );
};

interface IConnectedProps {
  basketAllIn: boolean;
  componentsFeature: guardComponentType;
  data: ITableReducer[TableNameType]['data'];
  fields: ITableReducer[TableNameType]['fields'];
  isDensity: boolean;
  isLoading: boolean;
  isTariff: boolean;
  productOfferingsTranslation: IWizardProductOfferingsTranslation;
  sort: ITableReducer[TableNameType]['sort'];
  subsCount: number;
  tableCount: number;
  translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>(
  {
    basketAllIn: makeSelectorTableBasketAllIn(
      TableNameType.productSelectSubscribers,
    ),
    componentsFeature: makeSelectGuardComponents(),
    data: makeSelectorTableData(TableNameType.productSelectSubscribers),
    fields: makeSelectorTableFields(TableNameType.productSelectSubscribers),
    isDensity: makeSelectIsDensity(),
    isLoading: makeSelectorTableIsLoading(
      TableNameType.productSelectSubscribers,
    ),
    isTariff: makeSelectProductOfferingsIsTariff(),
    productOfferingsTranslation: makeSelectTranslationWizardProductOfferings(),
    sort: makeSelectorTableSort(TableNameType.productSelectSubscribers),
    subsCount: makeSelectProductOfferingsSubsCount(),
    tableCount: makeSelectorTableCount(TableNameType.productSelectSubscribers),
    translationCommon: makeSelectTranslationCommon(),
  },
);

interface IDispatchProps {
  loadTableList: (payload?: ILoadTableListPayload) => TLoadTableListAction;
  setTableSort: (payload: ISetTableSortPayload) => void;
  startValidateAction: () => void;
  switchCheckbox: (payload: ISwitchCheckboxPayload) => TSwitchCheckboxAction;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  loadTableList: (payload) => dispatch(loadTableListAction(payload)),
  setTableSort: (payload: ISetTableSortPayload) =>
    dispatch(setTableSortAction(payload)),
  startValidateAction: () => dispatch(productOfferingsStartValidateAction()),
  switchCheckbox: (payload: ISwitchCheckboxPayload) =>
    dispatch(switchCheckbox(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProductSelectSubscribersTable);
